/**
 * IT DRAFT CLM — tokens de diseño (rebranding 2026)
 *
 * Fuente de verdad del sistema visual para JavaScript y Ant Design.
 * Port tipado de `restructura/index.js` (entregable de diseño).
 *
 * IMPORTANTE: `src/scss/themes/general-theme.scss` declara las mismas
 * variables como CSS custom properties para que el primer render no
 * parpadee. Si cambias un valor aquí, cámbialo también allá.
 */

export const colors = {
  bg: '#F7F5EF', // fondo de la aplicación (crema)
  surface: '#FFFFFF', // tarjetas
  text: '#1E2B27', // tinta; también fondo de superficies oscuras
  textOnDark: '#F0F7F4',

  accent: '#0F8A7A', // verde de marca — acción principal
  accent100: '#DDF2EE',
  accent200: '#BEE5DE',
  accent300: '#8ECEC3',
  accent400: '#5BB3A6',
  accent600: '#0C7264', // hover del botón principal
  accent700: '#0A5F54', // texto de acento sobre claro
  accent800: '#084E45',
  accentBright: '#2BC4AF', // acento sobre superficies oscuras

  neutral100: '#F1EFE9',
  neutral200: '#E4E1D9',
  neutral300: '#D2CEC4',
  neutral400: '#B4AFA3',
  neutral500: '#918B7E',
  neutral600: '#6F6A5F',
  neutral700: '#514D45',
  neutral800: '#35322C',
  neutral900: '#22201C',

  // Paleta "cockpit" (entregable de Inicio): bosque para las superficies
  // oscuras de marca, menta para acentos esmeralda y ámbar para pendientes.
  forest: '#0B2923', // banner / superficies de marca oscuras
  forestDeep: '#09221D', // panel sobre bosque (telemetría)
  forestCard: '#0F342C', // mini-tarjeta sobre bosque
  forestChip: '#123830', // chips y píldoras sobre bosque
  forestBorder: '#144238', // borde del banner
  mint: '#10B981', // esmeralda del entregable (CTA, acentos)
  mintDeep: '#0DA270', // hover del esmeralda
  mintText: '#047857', // esmeralda legible sobre fondos claros
  mintSoft: '#ECFDF5', // fondo de chip claro
  mintBg: '#F4FBF8', // lienzo del dashboard
  mintBorder: '#E2ECE7', // borde de tarjetas claras
  amber: '#FBBF24', // pendiente de firma / atención
  amberDeep: '#B45309', // ámbar legible sobre claro
  amberSoft: '#FEF3C7', // fondo de chip ámbar (etapa por firmar)
  danger: '#C0392B', // rojo de la gráfica (etapa rechazado)
} as const;

export const typography = {
  fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
  weights: { regular: 400, medium: 500, semibold: 600, bold: 700, black: 800 },
} as const;

export const radii = {
  none: '0px',
  control: '12px', // inputs, botones
  cta: '14px', // botón de acción principal
  card: '18px',
  pill: '999px', // chips, etiquetas de estado, barras de progreso
  avatar: '50%',
} as const;

export const shadows = {
  sm: '0 2px 10px rgba(30,43,39,0.07)', // tarjeta en reposo
  md: '0 8px 24px rgba(30,43,39,0.10)', // tarjeta destacada, botón principal
  lg: '0 18px 48px rgba(30,43,39,0.16)', // panel lateral, tarjeta oscura, modal
} as const;

export const spacing = {
  gridGap: 20,
  cardPadding: 26,
  sectionGap: 26,
  pagePadding: 32,
  sidebarWidth: 268,
} as const;

export const controls = {
  height: { sm: 34, md: 44, lg: 48, cta: 52 },
  border: `1.5px solid ${colors.neutral300}`,
  borderStrong: `1.5px solid ${colors.neutral400}`,
  divider: `1px solid ${colors.neutral200}`,
} as const;

/**
 * Etapas del documento — color y etiqueta. Una etapa, no dos estatus.
 * El valor de `stage` lo calcula el API; aquí solo se pinta.
 */
export type DocumentStage = 'borrador' | 'vobo' | 'firma' | 'rechazado' | 'listo';

export const stages: Record<DocumentStage, { label: string; bg: string; fg: string }> = {
  borrador: { label: 'BORRADOR', bg: colors.neutral200, fg: colors.neutral800 },
  vobo: { label: 'EN VOBO', bg: colors.accent100, fg: colors.accent800 },
  firma: { label: 'POR FIRMAR', bg: colors.text, fg: colors.bg },
  // RECHAZADO no viene en el entregable de diseño: es la quinta etapa que
  // exige el modelo (statusVobo puede ser RECHAZADO) y el orden por urgencia
  // de Inicio. Rojo terroso para no romper la paleta.
  rechazado: { label: 'RECHAZADO', bg: '#F7DEDA', fg: '#8C2A1C' },
  listo: { label: 'LISTO', bg: colors.neutral200, fg: colors.neutral800 },
};

/** Theme para Ant Design 5. Se monta una sola vez, en App.tsx. */
export const antdTheme = {
  token: {
    colorPrimary: colors.accent,
    colorTextBase: colors.text,
    colorBgLayout: colors.bg,
    colorBgContainer: colors.surface,
    colorBorder: colors.neutral300,
    colorBorderSecondary: colors.neutral200,
    borderRadius: 12,
    borderRadiusLG: 18,
    fontFamily: typography.fontFamily,
    fontSize: 14,
    controlHeight: 44,
    boxShadow: shadows.sm,
    boxShadowSecondary: shadows.md,
  },
  components: {
    Button: { fontWeight: 700, primaryShadow: shadows.md, controlHeight: 44 },
    Card: { borderRadiusLG: 18, boxShadowTertiary: shadows.sm, paddingLG: 26 },
    Table: {
      headerBg: colors.surface,
      headerColor: colors.neutral700,
      borderColor: colors.neutral200,
    },
    Tag: { borderRadiusSM: 999 },
    Menu: {
      itemSelectedBg: colors.accent100,
      itemSelectedColor: colors.accent800,
      itemBorderRadius: 12,
    },
    Input: { activeBorderColor: colors.accent, hoverBorderColor: colors.accent400 },
  },
};

const tokens = { colors, typography, radii, shadows, spacing, controls, stages, antdTheme };

export default tokens;
